import type { DataSourceStatus } from "@adpilot/shared";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  StatusBar,
  StatusBarPlot,
  type StatusBarStatus,
  StatusBarTooltip,
} from "@/components/charts/status-bar";

export const FRESHNESS_COLORS = {
  synced: "#059669",
  recent: "#2563eb",
  pending: "#d97706",
  attention: "#e11d48",
  unknown: "var(--muted-foreground)",
} as const;

type Day = NonNullable<DataSourceStatus["reportQuality"]>["days"][number];

interface DataFreshnessBarProps {
  days: Day[];
  shopName: string;
  sourceName: string;
  secondaryDays?: Day[];
  secondaryName?: string;
}

export function DataFreshnessBar({
  days,
  shopName,
  sourceName,
  secondaryDays,
  secondaryName,
}: DataFreshnessBarProps) {
  const { t, i18n } = useTranslation("settings");
  const formatters = useMemo(
    () => ({
      short: new Intl.DateTimeFormat(i18n.language, {
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      }),
    }),
    [i18n.language],
  );
  const formatDate = (date: string) => formatters.short.format(new Date(`${date}T00:00:00Z`));

  if (!days.length) {
    return <p className="text-xs text-muted-foreground">{t("dataStatus.daysUnavailable")}</p>;
  }

  const statuses: StatusBarStatus[] = [
    { id: "synced", label: t("dataStatus.dayStates.synced"), color: FRESHNESS_COLORS.synced },
    { id: "recent", label: t("dataStatus.dayStates.recent"), color: FRESHNESS_COLORS.recent },
    { id: "pending", label: t("dataStatus.dayStates.pending"), color: FRESHNESS_COLORS.pending },
    {
      id: "attention",
      label: t("dataStatus.dayStates.attention"),
      color: FRESHNESS_COLORS.attention,
    },
    {
      id: "unknown",
      label: t("dataStatus.dayStates.unknown"),
      color: FRESHNESS_COLORS.unknown,
    },
  ];
  const secondaryByDate = new Map(secondaryDays?.map((day) => [day.date, day]));
  const dayStatus = (day: Day | undefined) =>
    !day ? "unknown" : day.status === "synced" && day.recent ? "recent" : day.status;
  const secondaryColors: Record<string, string> = {};
  const data = days.map((day) => {
    const secondary = dayStatus(secondaryByDate.get(day.date));
    secondaryColors[day.date] = FRESHNESS_COLORS[secondary];
    return {
      id: day.date,
      label: formatDate(day.date),
      status: dayStatus(day),
      description: secondaryName
        ? `${sourceName}: ${t(`dataStatus.dayStates.${dayStatus(day)}`)}; ${secondaryName}: ${t(`dataStatus.dayStates.${secondary}`)}`
        : undefined,
    };
  });
  return (
    <StatusBar
      data={data}
      className="max-w-[740px]"
      statuses={statuses}
      label={t("dataStatus.dayGroup", {
        shop: shopName,
        source: secondaryName ? `${sourceName} / ${secondaryName}` : sourceName,
      })}
    >
      <StatusBarPlot
        animate={false}
        secondaryColors={secondaryDays ? secondaryColors : undefined}
      />
      <StatusBarTooltip className="w-60 max-w-64 p-0">
        {({ datum, status }) => {
          const secondary = dayStatus(secondaryByDate.get(datum.id));
          const sources = [
            { name: sourceName, label: status.label, color: status.color },
            ...(secondaryDays && secondaryName
              ? [
                  {
                    name: secondaryName,
                    label: t(`dataStatus.dayStates.${secondary}`),
                    color: FRESHNESS_COLORS[secondary],
                  },
                ]
              : []),
          ];
          return (
            <div className="overflow-hidden rounded-lg">
              <div className="border-b border-line bg-muted/40 px-3 py-2 text-xs font-medium tabular-nums text-muted-foreground">
                {datum.label}
              </div>
              <div className="divide-y divide-line px-3">
                {sources.map((source) => (
                  <div key={source.name} className="flex items-center gap-2.5 py-2.5">
                    <span
                      aria-hidden="true"
                      className="h-7 w-1 shrink-0 rounded-full"
                      style={{ backgroundColor: source.color }}
                    />
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <span className="text-[11px] text-muted-foreground">{source.name}</span>
                      <span className="text-xs font-medium text-foreground">{source.label}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        }}
      </StatusBarTooltip>
    </StatusBar>
  );
}
